import { useContext, useMemo } from 'react'
import { AddressDTO, LanguageDTODocLangKeyEnum, UserDTO } from '@ringo/shared'
import { useLocalStorage } from '../useLocalStorage'
import { AuthContext, UserDTOWithAddresses } from './AuthContext'
import { LanguageKey } from '@ringo/shared'
import { useAddressApi, useUserApi } from '../../api-context'
import { useQuery } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { useI18n } from '../../../useI18n'

export const useUser = () => {
    const { user, setUser: setContextUser } = useContext(AuthContext)
    const { setItem, getItem } = useLocalStorage()
    const userApi = useUserApi()
    const addressApi = useAddressApi()
    const { changeLanguage } = useI18n()
    const isAuthenticated: boolean = useMemo(() => !!user, [user])
    const isVerified = useMemo(() => user?.verificationStatus === 'VERIFIED', [user])

    const fetchCurrentUserQuery = useQuery({
        queryKey: ['fetchCurrentUser'],
        queryFn: async () => {
            let user: UserDTO | null
            let addresses: AddressDTO[] | null = null
            if (!getItem('token')) {
                user = null
                addresses = null
            } else {
                const userPromise = userApi.getCurrentUser()
                const addressPromise = addressApi.getMyAddresses()

                user = (await userPromise).data
                try {
                    addresses = (await addressPromise).data
                } catch (error) {
                    console.log(error)
                    addresses = []
                }
            }
            if (user) {
                setUser({ ...user, addresses: addresses })
                Sentry.setUser({ email: user.email, username: `${user.masterData?.firstName} ${user.masterData?.lastName}`, id: user.id })
            } else {
                setUser(null)
            }
            return user
        },
        enabled: false,
        retry: false,
    })

    const setUser = (user: UserDTOWithAddresses | null) => {
        setContextUser(user)
    }

    const removeUser = () => {
        setContextUser(null)
        Sentry.setUser(null)
        setItem('token', '')
    }

    const refreshUserData = async () => {
        return fetchCurrentUserQuery.refetch()
    }

    const extractTokenFromUrl = () => {
        const url = new URL(window.location.href)
        const searchParams = new URLSearchParams(url.search)
        return searchParams.get('token')
    }

    const updateUserLanguage = (language: LanguageKey) => {
        localStorage.setItem('lang', language as string)
        changeLanguage(language)
        if (user && user.langKey !== language.toUpperCase()) {
            userApi.updateLanguage({ langKey: language.toUpperCase() as any, docLangKey: user.docLangKey || LanguageDTODocLangKeyEnum.De })
            setUser({ ...user, langKey: language.toUpperCase() as any } as UserDTOWithAddresses)
        }
    }

    const isSapUser = useMemo(() => {
        return user?.salesOrg === 2500
    }, [user])

    return {
        user,
        isVerified,
        setUser,
        removeUser,
        isAuthenticated,
        refreshUserData,
        extractTokenFromUrl,
        updateUserLanguage,
        isSapUser,
        isLoading: fetchCurrentUserQuery.isPending,
    }
}
